import * as z from "zod";
import {
  anyOfFilters,
  code,
  codeName,
  cursorPagination,
  decimal,
  flag,
  pagination,
  validationIssue,
  version,
} from "../index";
import { paymentCategories } from "../reference/enums";
import { syncStatuses } from "../til";
import { hilldunApprovalDto } from "./credit";
import {
  creditOutcomes,
  fulfillmentIssues,
  orderConfirmationAnswers,
  soChannels,
  soLotStatuses,
  soStatuses,
} from "./enums";
import { reason } from "./fields";
import { bcPaymentDto, paymentConfirmationDto } from "./payment";

// Sales order, header and lifecycle (50-api/sales.md §2.1; FR-M6-01…03).

/** Sales order by id (uuid) or by number. */
export const getSalesOrderInput = z.object({ key: z.string().min(1).max(36) });
export type GetSalesOrderInput = z.input<typeof getSalesOrderInput>;

/** Orders of the list; the id breaks ties. */
const salesOrderSorts = ["-updatedAt", "soNumber", "expectedDeliveryStart"] as const;

const salesOrderFilters = {
  status: z.enum(soStatuses).optional(),
  /** Neither completed nor cancelled: the orders in progress, the second part of the home of Frontrow (ADR-219). */
  open: flag.optional(),
  customer: z.uuid().optional(),
  brand: z.uuid().optional(),
  /** Code of the season. */
  season: code.optional(),
  /**
   * Spec v0.3 §6, rule 1b (C-56, ADR-259): an order the allocation still serves (not yet locked, or locked in lots)
   * without a season, which ranks as of no active season: the list of the Customer Service.
   */
  withoutSeason: flag.optional(),
  /** Only the ones Joor changed after their allocation: the change waits for the Customer Service (B-31). */
  joorChangePending: flag.optional(),
  /**
   * v0.2 §9, "orders waiting for payment": proforma terms, allocated or beyond and not yet released, with the flag
   * "payment completed" not raised.
   */
  awaitingPayment: flag.optional(),
  /** v0.2 §9, "deposits not paid": a deposit is due, and Business Central received less; open orders only. */
  depositDue: flag.optional(),
  /**
   * Allocated, or locked in lots with units in no lot yet, and nothing holds its lock back (`canLock` of the rules):
   * the queue "to lock" of the Customer Service (ADR-122, ADR-195).
   */
  lockable: flag.optional(),
  /**
   * Its credit blocked where Credit Control can override it (`canOverrideCredit` of the rules): stopped before its
   * allocation, or found blocked once allocated (Q-SAL-05). The queue of Credit Control (ADR-122).
   */
  creditBlocked: flag.optional(),
  /** How the order stands towards Business Central, which has it from its lock (I-03). */
  bcSyncStatus: z.enum(syncStatuses).optional(),
  /** The process of the order stopped, and waits for a person to resume it (ADR-133): the queue of the Customer Service. */
  fulfillmentIssue: flag.optional(),
  /**
   * Units a PO lowered by its supplier no longer brings, which the order could not lose because it was locked or
   * beyond (`canSettleUncovered` of the rules): the queue "orders locked without cover" of the Customer Service
   * (ADR-187).
   */
  uncovered: flag.optional(),
  /**
   * A proforma or a deposit still not paid one cadence after its last reminder (`sales_order.max_payment_reminders`):
   * the queue of Credit Control "dunning over" (ADR-178 §1.12, ADR-203).
   */
  dunningOver: flag.optional(),
  /**
   * Where the latest revision of the order confirmation stands with the customer (B-47, ADR-208), on an order not
   * cancelled nor completed: `awaiting` its answer (the confirmations to accept of Frontrow), `overdue` past
   * `sales_order.confirmation_answer_days`, `rejected`: the queues of the Customer Service.
   */
  confirmation: z.enum(orderConfirmationAnswers).optional(),
  /**
   * Spec v0.3 §9, "Conferme d'ordine" (C-45, ADR-261): an order neither completed nor cancelled that no order
   * confirmation was issued or asked for (a failed one aside): the queue of the Customer Service.
   */
  withoutConfirmation: flag.optional(),
  /** Part of the number of the order, in Nexxt, on Joor or the customer's PO, case-insensitive (ADR-219). */
  q: z.string().trim().min(1).max(60).optional(),
};

/** The orders in at least one of these sets of the filters (ADR-302): the queues of the sales orders, to do. */
const anyOfSalesOrders = { anyOf: anyOfFilters(salesOrderFilters).optional() };

/** The sales orders the Actor sees: all of them for Tomorrow, its own for a customer (RLS). */
export const listSalesOrdersInput = z.object({
  ...salesOrderFilters,
  ...anyOfSalesOrders,
  sort: z.enum(salesOrderSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListSalesOrdersInput = z.input<typeof listSalesOrdersInput>;

/** How many orders the list has with the same filters, and when the one untouched for longest was updated (ADR-086). */
export const countSalesOrdersInput = z.object({ ...salesOrderFilters, ...anyOfSalesOrders });
export type CountSalesOrdersInput = z.input<typeof countSalesOrdersInput>;

/**
 * `version` is the one the client read: an order changed in the meantime, in its header or in its lines, is left as
 * it is (409). The reason goes to `cancelReason` too.
 */
export const cancelSalesOrderInput = z.object({ salesOrderId: z.uuid(), version, reason });
export type CancelSalesOrderInput = z.input<typeof cancelSalesOrderInput>;
/** The gateway routes split the input: the id in the path, the rest in the body. */
export const salesOrderParams = cancelSalesOrderInput.pick({ salesOrderId: true });
export const cancelSalesOrderBody = cancelSalesOrderInput.omit({ salesOrderId: true });

/**
 * An action on an order that says nothing but the version the client read (409 if changed): locking it (spec v0.2
 * §5.2, ADR-120), sending it to Business Central again, resuming its process (ADR-133).
 */
export const salesOrderActionInput = z.object({ salesOrderId: z.uuid(), version });
export type SalesOrderActionInput = z.input<typeof salesOrderActionInput>;
export const salesOrderActionBody = salesOrderActionInput.omit({ salesOrderId: true });

/** ADR-178 §1.11, ADR-195: the most orders one bulk lock takes, each in its own transaction. */
export const LOCK_SALES_ORDERS_MAX = 25;

/**
 * ADR-178 §1.11: the Customer Service locks several orders at once from the queue "to lock", each with the version
 * it read. Each is locked, or refused, on its own.
 */
export const lockSalesOrdersInput = z.object({
  orders: z
    .array(salesOrderActionInput)
    .min(1)
    .max(LOCK_SALES_ORDERS_MAX)
    .refine((orders) => new Set(orders.map((o) => o.salesOrderId)).size === orders.length, {
      message: "an order is named more than once",
    }),
});
export type LockSalesOrdersInput = z.input<typeof lockSalesOrdersInput>;

/** What a bulk lock did to each order, in the order asked. */
export const lockSalesOrdersDto = z
  .object({
    orders: z.array(
      z.object({
        salesOrderId: z.uuid(),
        outcome: z.enum(["locked", "refused"]),
        /** The lot the lock froze; null when refused. */
        lotNo: z.number().int().nullable(),
        /** The version of the order after the lock; null when refused. */
        version: z.number().int().nullable(),
        /** Why the order was not locked: the code and the issues of the same refusal as one lock. */
        error: z.object({ code: z.string(), issues: z.array(validationIssue) }).nullable(),
      }),
    ),
  })
  .meta({ id: "LockSalesOrders" });
export type LockSalesOrdersResult = z.output<typeof lockSalesOrdersDto>;

/** The lots of an order, by number (ADR-195). */
export const listSalesOrderLotsInput = z.object({ salesOrderId: z.uuid() });
export type ListSalesOrderLotsInput = z.input<typeof listSalesOrderLotsInput>;

/**
 * A lot of an order (ADR-195): what one lock froze, with the shipment of logistics and where its process stands. The
 * order stays `locked` while its lots move.
 */
export const salesOrderLotDto = z
  .object({
    id: z.uuid(),
    lotNo: z.number().int(),
    status: z.enum(soLotStatuses),
    /** The shipment of logistics; null until the process created it. */
    fulfillmentId: z.uuid().nullable(),
    /** Units the lock froze, and how many of them left so far. */
    qty: z.number().int(),
    qtyFulfilled: z.number().int(),
    lockedAt: z.iso.datetime(),
    /** When the flag of the order paid it (proforma terms); null until then. */
    paidAt: z.iso.datetime().nullable(),
    shippedAt: z.iso.datetime().nullable(),
    /** ADR-197: released without an invoice, and invoiced on the units shipped once it left. */
    invoiceAfterShipment: z.boolean(),
    /** Why the process of the lot stopped (ADR-133); null while it runs. */
    fulfillmentIssue: z.enum(fulfillmentIssues).nullable(),
    fulfillmentIssueCode: z.string().nullable(),
    fulfillmentIssueAt: z.iso.datetime().nullable(),
    version: z.number().int(),
  })
  .meta({ id: "SalesOrderLot" });
export type SalesOrderLot = z.output<typeof salesOrderLotDto>;

/**
 * ADR-187: the Customer Service handled the units no PO brings any more of a locked order (told the customer, or
 * changed the order on Business Central), and says how: they leave the queue. `version` as for a cancellation.
 */
export const settleUncoveredUnitsInput = z.object({ salesOrderId: z.uuid(), version, reason });
export type SettleUncoveredUnitsInput = z.input<typeof settleUncoveredUnitsInput>;
export const settleUncoveredUnitsBody = settleUncoveredUnitsInput.omit({ salesOrderId: true });

/**
 * The warehouse that ships an order, until it is allocated (ADR-113): one of the company that sells. `version` as for
 * a cancellation.
 */
export const assignSalesOrderWarehouseInput = z.object({ salesOrderId: z.uuid(), version, locationId: z.uuid() });
export type AssignSalesOrderWarehouseInput = z.input<typeof assignSalesOrderWarehouseInput>;
export const assignSalesOrderWarehouseBody = assignSalesOrderWarehouseInput.omit({ salesOrderId: true });

/** The values of the filters of the orders and of the warehouse of one (ADR-048, ADR-122): nothing to choose. */
export const getSalesOrderOptionsInput = z.object({});
export type GetSalesOrderOptionsInput = z.input<typeof getSalesOrderOptionsInput>;

/**
 * The values the screens of the orders offer (ADR-122): brands and seasons of the orders the Actor sees, and the
 * warehouses in use, each with the company it ships for: an order takes only one of its own company (ADR-113).
 */
export const salesOrderOptionsDto = z
  .object({
    brands: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
    /** With the id the runs of the order confirmations name (ADR-202). */
    seasons: z.array(codeName.extend({ id: z.uuid() })),
    warehouses: z.array(z.object({ id: z.uuid(), code: z.string(), legalEntity: z.string(), countryCode: z.string() })),
  })
  .meta({ id: "SalesOrderOptions" });
export type SalesOrderOptions = z.output<typeof salesOrderOptionsDto>;

/** Newest first. */
export const getSalesOrderHistoryInput = z.object({
  salesOrderId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetSalesOrderHistoryInput = z.input<typeof getSalesOrderHistoryInput>;
export const salesOrderHistoryQuery = getSalesOrderHistoryInput.omit({ salesOrderId: true });

/** An address an order carries of its own, as the channel gave it. */
export const salesOrderAddressDto = z.object({
  name: z.string().nullable(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string(),
});
export type SalesOrderAddress = z.output<typeof salesOrderAddressDto>;

/** The addresses an order came with: `sales_order.address_snapshot`. */
export interface SalesOrderAddresses {
  billTo: SalesOrderAddress | null;
  shipTo: SalesOrderAddress | null;
}

/**
 * A sales order: in the list, alone and after an action. Totals come from the lines not cancelled; the amount is
 * after the discounts of the lines and of the header.
 */
export const salesOrderDto = z
  .object({
    id: z.uuid(),
    soNumber: z.string(),
    status: z.enum(soStatuses),
    channel: z.enum(soChannels),
    customerOrgId: z.uuid(),
    customer: z.string(),
    brandOrgId: z.uuid().nullable(),
    brand: z.string().nullable(),
    /** Code of the company of the group that sells. */
    legalEntity: z.string(),
    season: z.string().nullable(),
    /** Code of the warehouse that ships the order; null until somebody says, and a run leaves the order out. */
    warehouse: z.string().nullable(),
    currency: z.string(),
    headerDiscountPct: decimal.nullable().optional(),
    paymentTerms: z.string().nullable(),
    deliveryTerm: z.string().nullable(),
    incoterm: z.string().nullable(),
    expectedDeliveryStart: z.iso.date().nullable(),
    expectedDeliveryEnd: z.iso.date().nullable(),
    /** The addresses the order came with; null, the defaults of the customer hold. */
    billTo: salesOrderAddressDto.nullable(),
    shipTo: salesOrderAddressDto.nullable(),
    memo: z.string().nullable(),
    creditOutcome: z.enum(creditOutcomes),
    /** The customer is insured by Hilldun: its order goes only with the approval of Hilldun (ADR-179, ADR-185). */
    hilldunInsured: z.boolean().optional(),
    /** What Hilldun said of the order; null until Credit Control records it. */
    hilldunApproval: hilldunApprovalDto.nullable().optional(),
    /** Of the payment terms: `proforma` is paid before the goods leave the warehouse. Null without terms. */
    paymentCategory: z.enum(paymentCategories).nullable(),
    /** FR-M10-02: the flag Credit Control raises; on proforma terms it holds the release back. */
    paymentCompleted: z.boolean(),
    /** The confirmation the flag stands on; null while it is down. */
    paymentConfirmation: paymentConfirmationDto.nullable().optional(),
    /** Deposit the payment terms ask for, percent of the order. */
    depositPct: decimal.nullable().optional(),
    /** The same deposit on the total of the order, in its currency. */
    depositRequiredAmount: decimal.nullable().optional(),
    /** What Business Central received and invoiced for the order; null until it says (I-06). */
    bcPayment: bcPaymentDto.nullable().optional(),
    lineCount: z.number().int(),
    openLineCount: z.number().int(),
    qtyOrdered: z.number().int(),
    qtyAllocated: z.number().int(),
    /** In the currency of the order. */
    totalAmount: decimal.optional(),
    /** Number of the order on Joor, as the customer sees it; null for an order of another channel. */
    joorOrderNumber: z.string().nullable(),
    /** Purchase order number the customer gave. */
    customerPoNumber: z.string().nullable(),
    /**
     * The claim whose goods the order replaces, without a price, and the order claimed (B-75, ADR-251); null for an
     * order of another channel.
     */
    replaces: z.object({ claimId: z.uuid(), salesOrderId: z.uuid(), soNumber: z.string() }).nullable(),
    /** Joor has a later state of the order that was not applied, and waits for the Customer Service (B-31). */
    joorChangePending: z.boolean(),
    /** When the allocation was first frozen: from then on Business Central has the order (I-03). */
    lockedAt: z.iso.datetime().nullable(),
    /** The lots the order was locked in (ADR-195): 0 before its lock, and for an order locked before the lots. */
    lotCount: z.number().int(),
    /** Their statuses, by number. */
    lotStatuses: z.array(z.enum(soLotStatuses)),
    /** Units committed to the order and in no lot yet: what the next lock freezes. */
    qtyToLock: z.number().int(),
    /** `not_synced` until the lock. */
    bcSyncStatus: z.enum(syncStatuses),
    /** `revision` of the last I-03 sent: 0 before the first. */
    bcSyncRevision: z.number().int(),
    /** Why the process of the order stopped (ADR-133); null while it runs, or before the lock. */
    fulfillmentIssue: z.enum(fulfillmentIssues).nullable(),
    /** The step it stopped at, with what was answered there: the code of a refusal, or what did not come. */
    fulfillmentIssueCode: z.string().nullable(),
    fulfillmentIssueAt: z.iso.datetime().nullable(),
    /**
     * Units a PO lowered by its supplier no longer brings, which the order could not lose because it was locked or
     * beyond, until the Customer Service settles them (ADR-187). The lines say which (`qtyUncovered`).
     */
    uncoveredUnits: z.number().int(),
    cancelledAt: z.iso.datetime().nullable(),
    cancelReason: z.string().nullable(),
    /**
     * The fee Credit Control applied to the order cancelled, in its currency (B-72, ADR-222); null without one. Finance
     * invoices it on Business Central.
     */
    cancellationFeeAmount: decimal.nullable().optional(),
    version: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "SalesOrder" });
export type SalesOrder = z.output<typeof salesOrderDto>;
